import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import Dropdown from 'components/Inputs/Dropdown';
import MetricPicker from 'components/Inputs/MetricPicker';
import AvailableWorkSpacesContext from 'contexts/AvailableWorkSpacesContext';
import DashboardsContext from 'contexts/DashboardsContext';
import ReportsContext from 'contexts/ReportsContext';

import { TRIGGER_KIND_LABELS, describeTrigger } from './constants';

// What makes a section fire. Every control here adds an id (or a phrase) that
// the SERVER matches deterministically — nothing on this screen decides
// anything at answer time, which is the whole point of the triggered tier.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.button`
  border: 1px solid var(--color-border-secondary);
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  padding: 2px 8px;
`;

const FieldDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 520px;
`;

const sameTrigger = (a: AccountKb.Trigger, b: AccountKb.Trigger): boolean =>
  describeTrigger(a) === describeTrigger(b);

const TriggerEditor = ({
  triggers,
  onChange,
  isDisabled,
}: {
  triggers: AccountKb.Trigger[];
  onChange: (triggers: AccountKb.Trigger[]) => void;
  isDisabled?: boolean;
}) => {
  const { availableWorkSpaces } = useContext(AvailableWorkSpacesContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { allReports } = useContext(ReportsContext);
  const [phrase, setPhrase] = useState<string>('');
  const [field, setField] = useState<string>('');

  const add = (trigger: AccountKb.Trigger) => {
    if (triggers.some((existing) => sameTrigger(existing, trigger))) {
      return;
    }
    onChange([...triggers, trigger]);
  };

  const remove = (trigger: AccountKb.Trigger) => {
    onChange(triggers.filter((existing) => !sameTrigger(existing, trigger)));
  };

  const contentOptions = [
    ...allReports.map((report) => ({
      label: `Report: ${report.name}`,
      id: report.id,
    })),
    ...allDashboards.map((dashboard) => ({
      label: `Dashboard: ${dashboard.name}`,
      id: dashboard.id,
    })),
  ].map((item) => ({
    key: item.id,
    label: item.label,
    isSelected: triggers.some(
      (trigger) => trigger.kind === 'content' && trigger.id === item.id,
    ),
    onSelected: () => add({ kind: 'content', id: item.id }),
  }));

  const workspaceOptions = availableWorkSpaces.map((workspace) => ({
    key: workspace.id,
    label: workspace.title,
    isSelected: triggers.some(
      (trigger) => trigger.kind === 'workspace' && trigger.id === workspace.id,
    ),
    onSelected: () => add({ kind: 'workspace', id: workspace.id }),
  }));

  return (
    <Wrapper data-testid="agent-kb-trigger-editor">
      <ChipRow>
        {triggers.length === 0 && (
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            No triggers yet — a triggered section with none can never reach an
            answer.
          </Typography.Body>
        )}
        {triggers.map((trigger) => (
          <Chip
            key={describeTrigger(trigger)}
            type="button"
            disabled={isDisabled}
            onClick={() => remove(trigger)}
            data-testid={`agent-kb-trigger-${describeTrigger(trigger)}`}
          >
            <Row centerAlign style={{ gap: 6 }}>
              <Badge
                badgeType="Default"
                text={TRIGGER_KIND_LABELS[trigger.kind]}
              />
              <Typography.Body type="Body 12">
                {trigger.kind === 'phrase' || trigger.kind === 'field'
                  ? trigger.value
                  : trigger.id}
              </Typography.Body>
              <Typography.Body
                type="Body 12"
                color="var(--color-text-secondary)"
              >
                ✕
              </Typography.Body>
            </Row>
          </Chip>
        ))}
      </ChipRow>

      <FieldDiv>
        <Typography.Body type="Label">When the question says…</Typography.Body>
        <Row centerAlign style={{ gap: 8 }}>
          <TextInput
            value={phrase}
            onChange={(event) => setPhrase(event.target.value)}
            placeholder="on-time delivery"
            data-testid="agent-kb-trigger-phrase"
          />
          <Button
            type="Secondary"
            size="Small"
            label="Add phrase"
            isDisabled={isDisabled || phrase.trim().length < 4}
            onClick={() => {
              add({ kind: 'phrase', value: phrase.trim() });
              setPhrase('');
            }}
          />
        </Row>
      </FieldDiv>

      <FieldDiv>
        <Typography.Body type="Label">
          When this metric is on screen
        </Typography.Body>
        <MetricPicker
          setMetricId={(metricId: string) => {
            if (metricId) {
              add({ kind: 'metric', id: metricId });
            }
          }}
          placeholder="Add a metric"
          testIdPrefix="agent-kb-trigger-metric"
        />
      </FieldDiv>

      <FieldDiv>
        <Typography.Body type="Label">
          When they are in this workspace
        </Typography.Body>
        <Dropdown
          options={workspaceOptions}
          placeholder="Add a workspace"
          isSearchEnabled
        />
      </FieldDiv>

      <FieldDiv>
        <Typography.Body type="Label">
          When they are looking at this content
        </Typography.Body>
        <Dropdown
          options={contentOptions}
          placeholder="Add a report or dashboard"
          isSearchEnabled
        />
      </FieldDiv>

      <FieldDiv>
        <Typography.Body type="Label">
          When this field is filtered or broken down by
        </Typography.Body>
        <Row centerAlign style={{ gap: 8 }}>
          <TextInput
            value={field}
            onChange={(event) => setField(event.target.value)}
            placeholder="terminal"
            data-testid="agent-kb-trigger-field"
          />
          <Button
            type="Secondary"
            size="Small"
            label="Add field"
            isDisabled={isDisabled || field.trim() === ''}
            onClick={() => {
              add({ kind: 'field', value: field.trim() });
              setField('');
            }}
          />
        </Row>
      </FieldDiv>
    </Wrapper>
  );
};

export default TriggerEditor;
